import type { PurchaseableType } from "./product"

export const studentEmailIds = [
	"welcome",
	"chapter-dripped",
	"event-reminder",
	"new-message",
	"new-community-post",
	"new-community-comment",
	"new-community-reply",
	"empty-student-created",
	"reset-password",
	"password-changed",
	"invite",
] as const

export type StudentEmailId = (typeof studentEmailIds)[number]

export type StudentEmailEditorDocument = {
	type: string
	attrs?: Record<string, unknown>
	content?: StudentEmailEditorDocument[]
	marks?: { type: string; attrs?: Record<string, unknown> }[]
	text?: string
}

/**
 * Emails authored before the visual editor keep sending, but the MJML and hand-written HTML surfaces are
 * gone, so those two kinds are only ever read. Editing one means replacing it with an `editor` document.
 */
export type StudentEmailContent =
	| { type: "editor"; document: StudentEmailEditorDocument }
	| { type: "mjml"; value: string }
	| { type: "html"; value: string }

export type LegacyStudentEmailContent = Extract<StudentEmailContent, { type: "mjml" | "html" }>

export function isLegacyStudentEmailContent(content: StudentEmailContent): content is LegacyStudentEmailContent {
	return content.type !== "editor"
}

/** Comfortably past anything the editor's own nesting limit allows, so only hostile input reaches it. */
const maxEditorNodeDepth = 100

export function isStudentEmailEditorDocument(value: unknown): value is StudentEmailEditorDocument {
	return isRecord(value) && value.type === "doc" && isEditorNode(value)
}

export function parseStudentEmailEditorDocument(value: string | null): StudentEmailEditorDocument | null {
	if (value === null) return null
	try {
		const parsed: unknown = JSON.parse(value)
		return isStudentEmailEditorDocument(parsed) ? parsed : null
	} catch {
		return null
	}
}

/**
 * Deliberately shape-only rather than an allowlist of node names: the editor gains nodes between releases,
 * and a stale list would reject documents it just wrote. Creators could already send hand-written HTML, so
 * the node set was never the trust boundary.
 */
function isEditorNode(root: unknown): boolean {
	const pending: { value: unknown; depth: number }[] = [{ value: root, depth: 0 }]

	while (pending.length > 0) {
		const { value, depth } = pending.pop()!

		// Walked iteratively with a depth cap rather than recursively, so a deeply nested payload fails
		// validation instead of overflowing the stack and turning a bad request into a 500.
		if (depth > maxEditorNodeDepth) return false
		if (!isRecord(value) || typeof value.type !== "string") return false
		if (value.type === "text" && typeof value.text !== "string") return false
		if (
			"marks" in value &&
			(!Array.isArray(value.marks) || !value.marks.every(mark => isRecord(mark) && typeof mark.type === "string"))
		) {
			return false
		}
		if ("attrs" in value && !isRecord(value.attrs)) return false
		if ("content" in value) {
			if (!Array.isArray(value.content)) return false
			for (const child of value.content) pending.push({ value: child, depth: depth + 1 })
		}
	}

	return true
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null
}

export type ThinProductAccessEmail = {
	id: number
	product: { type: PurchaseableType; id: number; name: string }
}

export type ProductAccessEmail = {
	id: number
	platformId: number
	product: { type: PurchaseableType; name: string; url: string }
	/** The row always carries the subject chosen when it was created, but it has no content until written. */
	custom: { title: string; content: StudentEmailContent | null }
}

export type LeadFormEmailForm = {
	title: string
	kind: "newsletter" | "inquiry"
	pages: { path: string; title: string }[]
}

/**
 * One row per form on the website, whether or not it has a reply email yet, plus any reply email whose form
 * has since been removed from the site so that it can still be deleted.
 */
export type ThinLeadFormEmail = {
	formKey: string
	form: LeadFormEmailForm | null
	email: { hasContent: boolean } | null
}

export type LeadFormEmail = {
	formKey: string
	/** Null until the block is saved as part of the website, or once it is removed again. */
	form: LeadFormEmailForm | null
	/** The row is created on the first save, so it is null until the creator writes the email. */
	custom: { title: string; content: StudentEmailContent | null } | null
}
